import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Star, MessageSquare, AlertTriangle, Phone, MessageCircle, ChevronDown, ChevronUp } from 'lucide-react';
import DateRangeFilter from '../components/cx/DateRangeFilter';
import { format, startOfMonth } from 'date-fns';
import { fmtDateTimeIST } from '../lib/tz';


const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ratingCls = v => v == null ? 'text-[#5C6B64]' : v >= 4 ? 'text-[#2D6A4F]' : v >= 3 ? 'text-[#D4A017]' : 'text-[#C45A41]';

export default function ManagerCustomerFeedback() {
  const { getHeaders } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  // Default: MTD (preserves the original page intent)
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const mtdStartStr = format(startOfMonth(new Date()), 'yyyy-MM-dd');
  const [range, setRange] = useState({ key: 'mtd', start_date: mtdStartStr, end_date: todayStr, label: 'MTD' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: range.start_date,
        end_date: range.end_date,
      });
      const { data } = await axios.get(`${API}/manager/feedback/summary?${params.toString()}`, { headers: getHeaders() });
      setSummary(data);
    } catch { setSummary({ range: {}, recent: [] }); }
    finally { setLoading(false); }
  }, [getHeaders, range.start_date, range.end_date]);

  useEffect(() => { load(); }, [load]);

  // Range-aware aggregate (backend returns `range` for windowed mode).
  const m = useMemo(() => summary?.range || summary?.mtd || {}, [summary]);
  const recent = summary?.recent || [];
  const sentimentTotal = (m.positive || 0) + (m.neutral || 0) + (m.negative || 0);
  const pct = n => sentimentTotal > 0 ? Math.round((n / sentimentTotal) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#F9F9F8] pb-10">
      <div className="max-w-4xl mx-auto px-4 pt-6" data-testid="manager-feedback-page">
        <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-[#1A2421] tracking-tight">Customer Feedback</h1>
            <p className="text-xs text-[#5C6B64] mt-0.5">QR-based dine-in &amp; delivery feedback · sorted with negative first</p>
          </div>
          <DateRangeFilter value={range} onChange={setRange} testId="mgr-feedback-range" />
        </div>

        {(loading || !summary) ? (
          <div className="flex items-center justify-center py-16"><div className="animate-spin w-8 h-8 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" /></div>
        ) : (
        <>
        {/* Range-aware summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="summary-cards">
          <Card className="border-[#E2E8DF] shadow-sm">
            <CardContent className="py-3 px-4 text-center">
              <p className="text-[10px] uppercase tracking-wider text-[#5C6B64]">Avg rating</p>
              <p className={`text-2xl font-bold tabular-nums ${ratingCls(m.avg)}`} data-testid="range-avg">
                {m.avg != null ? m.avg.toFixed(2) : '—'}
              </p>
              <p className="text-[10px] text-[#5C6B64]">{m.count || 0} responses</p>
            </CardContent>
          </Card>
          <Card className="border-[#E2E8DF] shadow-sm">
            <CardContent className="py-3 px-4 text-center">
              <p className="text-[10px] uppercase tracking-wider text-[#5C6B64]">Dine-in</p>
              <p className="text-2xl font-bold tabular-nums text-[#1A2421]">{m.dinein_count || 0}</p>
              <p className="text-[10px] text-[#5C6B64]">in range</p>
            </CardContent>
          </Card>
          <Card className="border-[#E2E8DF] shadow-sm">
            <CardContent className="py-3 px-4 text-center">
              <p className="text-[10px] uppercase tracking-wider text-[#5C6B64]">Delivery</p>
              <p className="text-2xl font-bold tabular-nums text-[#1A2421]">{m.delivery_count || 0}</p>
              <p className="text-[10px] text-[#5C6B64]">in range</p>
            </CardContent>
          </Card>
          <Card className="border-[#E2E8DF] shadow-sm">
            <CardContent className="py-3 px-4 text-center">
              <p className="text-[10px] uppercase tracking-wider text-[#5C6B64]">Action required</p>
              <p className={`text-2xl font-bold tabular-nums ${m.action_required ? 'text-[#C45A41]' : 'text-[#2D6A4F]'}`} data-testid="action-required">
                {m.action_required || 0}
              </p>
              <p className="text-[10px] text-[#5C6B64]">rating ≤ 2</p>
            </CardContent>
          </Card>
        </div>

        {/* Sentiment split */}
        {sentimentTotal > 0 && (
          <Card className="border-[#E2E8DF] shadow-sm mb-4" data-testid="sentiment-split">
            <CardContent className="py-3 px-4">
              <p className="text-[10px] uppercase tracking-wider text-[#5C6B64] mb-2">Sentiment split ({range.label})</p>
              <div className="flex h-3 rounded-full overflow-hidden bg-[#E2E8DF]">
                <div className="bg-[#2D6A4F]" style={{ width: `${pct(m.positive)}%` }} />
                <div className="bg-[#D4A017]" style={{ width: `${pct(m.neutral)}%` }} />
                <div className="bg-[#C45A41]" style={{ width: `${pct(m.negative)}%` }} />
              </div>
              <div className="flex items-center justify-between text-[11px] mt-2">
                <span className="flex items-center gap-1 text-[#2D6A4F] font-medium">
                  <span className="w-2 h-2 rounded-full bg-[#2D6A4F]" /> Positive {m.positive} ({pct(m.positive)}%)
                </span>
                <span className="flex items-center gap-1 text-[#D4A017] font-medium">
                  <span className="w-2 h-2 rounded-full bg-[#D4A017]" /> Neutral {m.neutral} ({pct(m.neutral)}%)
                </span>
                <span className="flex items-center gap-1 text-[#C45A41] font-medium">
                  <span className="w-2 h-2 rounded-full bg-[#C45A41]" /> Negative {m.negative} ({pct(m.negative)}%)
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent feedback list — action required first */}
        <Card className="border-[#E2E8DF] shadow-sm">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-[color:var(--color-primary)]" />
              Recent feedback
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {recent.length === 0 ? (
              <p className="text-sm text-[#5C6B64] text-center py-6">
                No feedback yet. Print the QR codes from Admin → Customer Experience → QR & Landing and place them at tables or on delivery bills.
              </p>
            ) : (
              <div className="space-y-2" data-testid="recent-feedback-list">
                {recent.map(f => (
                  <div key={f.id}
                    className={`p-3 rounded-lg border ${f.action_required ? 'border-[#C45A41]/40 bg-[#FEF2EE]' : 'border-[#E2E8DF] bg-white'}`}
                    data-testid={`feedback-row-${f.id}`}>
                    <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-base font-bold tabular-nums flex items-center gap-0.5 ${ratingCls(f.overall_rating)}`}>
                          <Star className="w-3.5 h-3.5" fill="currentColor" /> {f.overall_rating}
                        </span>
                        <Badge variant="outline" className="text-[9px] uppercase tracking-wider">{f.form_type}</Badge>
                        {f.action_required && (
                          <Badge variant="destructive" className="text-[9px]">
                            <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                            Action required
                          </Badge>
                        )}
                        {f.name && <span className="text-[11px] text-[#1A2421] font-medium">{f.name}</span>}
                        {f.phone && (
                          <span className="flex items-center gap-1 text-[10px] text-[#5C6B64]">
                            <a href={`tel:+91${f.phone}`} title="Call"
                              data-testid={`mgr-call-${f.id}`}
                              className="w-6 h-6 rounded-full bg-[color:var(--color-primary)]/10 text-[color:var(--color-primary)] hover:bg-[color:var(--color-primary)] hover:text-white flex items-center justify-center transition-colors">
                              <Phone className="w-3 h-3" />
                            </a>
                            <a href={`https://wa.me/91${f.phone}`} target="_blank" rel="noopener noreferrer" title="WhatsApp"
                              data-testid={`mgr-whatsapp-${f.id}`}
                              className="w-6 h-6 rounded-full bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366] hover:text-white flex items-center justify-center transition-colors">
                              <MessageCircle className="w-3 h-3" />
                            </a>
                            <span className="font-mono tabular-nums">{f.phone}</span>
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] text-[#5C6B64]">{fmtDateTimeIST(f.submitted_at)}</span>
                    </div>
                    {f.comments && <p className="text-xs text-[#1A2421] mt-1 leading-relaxed">"{f.comments}"</p>}
                    <div className="flex items-center gap-3 text-[10px] text-[#5C6B64] mt-2">
                      {f.food_rating && <span>Food {f.food_rating}★</span>}
                      {f.service_rating && <span>Service {f.service_rating}★</span>}
                      {f.ambience_rating && <span>Ambience {f.ambience_rating}★</span>}
                      {f.packaging_rating && <span>Packaging {f.packaging_rating}★</span>}
                      {f.delivery_rating && <span>Delivery {f.delivery_rating}★</span>}
                    </div>
                    {Array.isArray(f.custom_responses) && f.custom_responses.length > 0 && (
                      <div className="mt-2" data-testid={`custom-responses-${f.id}`}>
                        <button type="button"
                          onClick={() => setExpanded(e => ({ ...e, [f.id]: !e[f.id] }))}
                          className="flex items-center gap-1 text-[10px] font-semibold text-[color:var(--color-primary)] hover:underline"
                          data-testid={`custom-toggle-${f.id}`}>
                          {expanded[f.id] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          {f.custom_responses.length} custom answer{f.custom_responses.length !== 1 ? 's' : ''}
                        </button>
                        {expanded[f.id] && (
                          <div className="mt-1.5 space-y-1 pl-3 border-l-2 border-[#E2E8DF]">
                            {f.custom_responses.map((r, i) => (
                              <div key={i} className="text-[11px] leading-tight">
                                <span className="text-[#5C6B64]">{r.question}:</span>{' '}
                                <span className="font-semibold text-[#1A2421]">
                                  {r.type === 'rating' ? `${r.answer}★` : String(r.answer)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        </>
        )}
      </div>
    </div>
  );
}
